// src/pages/Leaderboard.jsx
import React, { useEffect, useState } from 'react';
import { secureFetch } from '../utils/secureFetch';

const API_BASE = process.env.REACT_APP_API_BASE;

export default function Leaderboard() {
  const [loading, setLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState([]);
  const role = localStorage.getItem("beercard_role");

  // Admin-only (per your latest rule set)
  if (role !== "admin") {
    return <p className="p-4 text-red-600 font-semibold">❌ You must be an admin to view the leaderboard.</p>;
  }

  useEffect(() => {
    const load = async () => {
      try {
        // Ensure we know the current team/club (from LS or /user)
        let teamId = localStorage.getItem("beercard_teamId");
        let clubId = localStorage.getItem("beercard_clubId");

        if (!teamId || !clubId) {
          const me = await secureFetch(`${API_BASE}/user`);
          teamId = me.teamId || me.team || teamId;
          clubId = me.clubId || me.club || clubId;
          if (teamId) localStorage.setItem("beercard_teamId", teamId);
          if (clubId) localStorage.setItem("beercard_clubId", clubId);
        }

        if (!teamId || !clubId) {
          setLeaderboard([]);
          setLoading(false);
          return;
        }

        const [users, txs] = await Promise.all([
          secureFetch(`${API_BASE}/users`),
          secureFetch(`${API_BASE}/transactions/all`)
        ]);

        // Filter users for THIS team/club (support legacy fields)
        const filteredUsers = users.filter(u =>
          u.status === "approved" &&
          (u.teamId === teamId || u.team === teamId) &&
          (u.clubId === clubId || u.club === clubId)
        );

        // Build drink counts/spend per user from transactions
        const drinkMap = {};
        txs.forEach(tx => {
          const amt = typeof tx.amount === "string" ? parseFloat(tx.amount) : tx.amount;

          // Heuristic: a “drink” is a negative amount that isn't a top-up/admin adjustment
          const desc = tx.description || "";
          const isTopUp = /top-?up/i.test(desc);
          const isAdminAdj = /admin balance update/i.test(desc);
          const isFine = tx.type === "fine" || /^fine:/i.test(desc);
          const isCollection = tx.type === "collection" || /^collection contribution:/i.test(desc);

          const isDrink =
            typeof amt === "number" &&
            amt < 0 &&
            !isTopUp &&
            !isAdminAdj &&
            !isFine &&
            !isCollection;

          if (isDrink) {
            const key = tx.email;

            if (!drinkMap[key]) {
              drinkMap[key] = {
                count: 0,
                spent: 0
              };
            }

            drinkMap[key].count += 1;
            drinkMap[key].spent += Math.abs(amt);
          }
        });

        // Merge & sort
        const sorted = filteredUsers
          .map(u => ({
            name: `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() || u.email,
            email: u.email,
            team: u.teamId || u.team,
            joined: u.createdAt,
            count: drinkMap[u.email]?.count || 0,
            spent: drinkMap[u.email]?.spent || 0
          }))
          .sort((a, b) => (b.count - a.count) || (b.spent - a.spent));

        setLeaderboard(sorted);
      } catch (err) {
        console.error("Failed to load leaderboard:", err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--tk-navy)] text-white p-4">
        <p>Loading leaderboard...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--tk-navy)] text-white">
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="mb-5">
          <h1 className="text-2xl font-bold">Leaderboard</h1>
          <p className="text-sm text-white/70">Most drinks redeemed by approved players in this team.</p>
        </div>

        {leaderboard.length === 0 ? (
          <div className="rounded-2xl bg-white/10 border border-white/15 p-5 text-white/80 italic">
            No drinks recorded for this club/team yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {leaderboard.map((u, index) => {
              const rank = index + 1;
              const isTopThree = rank <= 3;

              return (
                <div
                  key={u.email}
                  className="rounded-2xl bg-white text-slate-900 shadow-lg border border-white/20 p-4 flex flex-col gap-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`inline-flex items-center justify-center w-9 h-9 rounded-full font-extrabold ${isTopThree ? 'bg-yellow-100 text-yellow-800' : 'bg-slate-100 text-slate-700'}`}>
                          #{rank}
                        </span>
                        <h3 className="text-lg font-extrabold leading-tight">{u.name}</h3>
                      </div>
                      <p className="text-xs text-slate-500 break-all">{u.email}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-xl bg-slate-100 p-3">
                      <p className="text-xs uppercase tracking-wide text-slate-500">Drinks</p>
                      <p className="text-2xl font-extrabold">🍻 {u.count}</p>
                    </div>
                    <div className="rounded-xl bg-slate-100 p-3">
                      <p className="text-xs uppercase tracking-wide text-slate-500">Spent</p>
                      <p className="text-2xl font-extrabold">${u.spent.toFixed(2)}</p>
                    </div>
                  </div>

                  <div className="mt-auto border-t border-slate-200 pt-3 text-sm text-slate-600 space-y-1">
                    <p>Team: <span className="font-semibold text-slate-900">{u.team || '-'}</span></p>
                    <p>Joined: <span className="font-semibold text-slate-900">{u.joined ? new Date(u.joined).toLocaleDateString('en-GB') : '-'}</span></p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
